import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { supabase } from "@/integrations/supabase/client";

const SITE = "Light of Life International";

async function fetchPost(slug: string) {
  const { data, error } = await supabase
    .from("posts")
    .select("*, media:featured_media_id(public_url, alt_text)")
    .eq("slug", slug)
    .eq("status", "published")
    .maybeSingle();
  if (error) throw error;
  return data;
}

function summarize(post: { excerpt?: string | null; content?: string | null } | null) {
  const raw = post?.excerpt || post?.content || "";
  const text = raw
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return `A ministry update from ${SITE}.`;
  return text.length > 155 ? `${text.slice(0, 152).trimEnd()}…` : text;
}

export const Route = createFileRoute("/blog_/$slug")({
  loader: async ({ params }) => ({ post: await fetchPost(params.slug) }),
  head: ({ params, loaderData }) => {
    const post = loaderData?.post ?? null;
    const title = post?.title ? `${post.title} — ${SITE}` : `Post Not Found — ${SITE}`;
    const description = summarize(post);
    const image = post?.media?.public_url;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://loli.ardent.dev/blog/${params.slug}` },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: `https://loli.ardent.dev/blog/${params.slug}` }],
      scripts: post
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                headline: post.title,
                description,
                ...(image ? { image } : {}),
                ...(post.published_at ? { datePublished: post.published_at } : {}),
                ...(post.updated_at ? { dateModified: post.updated_at } : {}),
                mainEntityOfPage: `https://loli.ardent.dev/blog/${params.slug}`,
                publisher: {
                  "@type": "Organization",
                  name: SITE,
                  url: "https://loli.ardent.dev/",
                },
              }),
            },
          ]
        : [],
    };
  },
  component: PostPage,
});

function PostPage() {
  const { slug } = Route.useParams();
  const { post: initialPost } = Route.useLoaderData();

  const { data: post, isLoading } = useQuery({
    queryKey: ["post", slug],
    queryFn: () => fetchPost(slug),
    initialData: initialPost,
  });

  return (
    <div className="min-h-dvh bg-background">
      <Header overlay={false} />
      <main id="main" className="container-site max-w-3xl section-y">
        {isLoading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : post ? (
          <article className="flex flex-col gap-6">
            <h1 className="text-4xl text-navy sm:text-5xl">{post.title}</h1>
            {post.published_at ? (
              <p className="eyebrow text-xs text-muted-foreground">
                {new Date(post.published_at).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            ) : null}
            {post.media?.public_url ? (
              <img
                src={post.media.public_url}
                alt={post.media.alt_text ?? ""}
                className="w-full rounded-lg object-cover"
              />
            ) : null}
            {post.content ? (
              <div
                className="flex flex-col gap-5 [&_h2]:mt-6 [&_h2]:text-2xl [&_h2]:text-navy [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            ) : null}
          </article>
        ) : (
          <div className="flex flex-col gap-4">
            <h1 className="text-4xl text-navy">Post not found</h1>
            <Link to="/blog" className="font-heading text-sm uppercase tracking-widest underline">
              Back to the blog
            </Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
